import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { GlassCard } from '@/components/ui/glass-card';
import {
  X,
  ZoomIn,
  ZoomOut,
  Download,
  Share2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

export default function GalleryPage() {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [zoom, setZoom] = useState(1);

  const images = [
    { url: "/images/1.jpeg", category: "students" },

    ...Array.from({ length: 95 }, (_, i) => ({
      url: `/images/1_${String(i + 2).padStart(2, "0")}.jpeg`,
      category: "students"
    }))
  ];

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;

  // 🔥 Keyboard Controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;

      if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex]);

  const closeModal = () => {
    setSelectedIndex(null);
    setZoom(1);
  };

  const nextImage = () => {
    setSelectedIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
    setZoom(1);
  };

  const prevImage = () => {
    setSelectedIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
    setZoom(1);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = selectedImage.url;
    link.download = selectedImage.title || 'gallery-image';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: selectedImage.title,
        url: window.location.origin + selectedImage.url
      });
    } else {
      navigator.clipboard.writeText(window.location.origin + selectedImage.url);
      alert('Image link copied to clipboard');
    }
  };

  return (
    <Layout>
      {/* HERO */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-950 text-white text-center">
        <h1 className="text-5xl md:text-7xl font-bold mb-6">Photo Gallery</h1>
        <p className="text-xl text-blue-100">Capturing moments of excellence</p>
      </section>

      {/* GRID */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((img, i) => (
            <GlassCard
              key={i}
              className="overflow-hidden p-0 cursor-pointer hover:scale-105 transition"
              onClick={() => {
                setSelectedIndex(i);
                setZoom(1);
              }}
            >
              <img
                src={img.url}
                alt={img.title}
                className="w-full h-64 object-cover"
              />
            </GlassCard>
          ))}
        </div>
      </section>

      {/* LIGHTBOX MODAL */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">

          {/* Close */}
          <button
            onClick={closeModal}
            className="absolute top-6 right-6 bg-black/50 text-white p-3 rounded-full hover:bg-red-600"
          >
            <X size={24} />
          </button>

          {/* Top Controls */}
          <div className="absolute top-6 left-6 flex gap-3">

            <button
              onClick={() => setZoom((z) => z + 0.2)}
              className="bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
            >
              <ZoomIn />
            </button>

            <button
              onClick={() => setZoom((z) => (z > 0.6 ? z - 0.2 : z))}
              className="bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
            >
              <ZoomOut />
            </button>

            <button
              onClick={handleDownload}
              className="bg-black/50 text-white p-3 rounded-full hover:bg-green-600"
            >
              <Download />
            </button>

            <button
              onClick={handleShare}
              className="bg-black/50 text-white p-3 rounded-full hover:bg-purple-600"
            >
              <Share2 />
            </button>
          </div>

          {/* Left Arrow */}
          <button
            onClick={prevImage}
            className="absolute left-6 bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
          >
            <ChevronLeft size={32} />
          </button>

          {/* Right Arrow */}
          <button
            onClick={nextImage}
            className="absolute right-6 bg-black/50 text-white p-3 rounded-full hover:bg-blue-600"
          >
            <ChevronRight size={32} />
          </button>

          {/* Image */}
          <img
            src={selectedImage.url}
            alt={selectedImage.title}
            style={{ transform: `scale(${zoom})` }}
            className="max-h-[85vh] max-w-[90vw] transition-transform duration-300"
          />

          {/* Image Counter */}
          <div className="absolute bottom-6 text-white text-sm">
            {selectedIndex + 1} / {images.length}
          </div>

          {/* Click Outside */}
          <div
            className="absolute inset-0 -z-10"
            onClick={closeModal}
          ></div>
        </div>
      )}
    </Layout>
  );
}